/**
 * AgentPage - Main Harry AI Agent Interface
 * 
 * Provides a full-screen chat interface with conversation history.
 * Only accessible to practitioners.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Bot, Plus, Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useClient } from '@/context/client-context';
import PageLayout from '@/components/shared/PageLayout';
import AgentChat from './AgentChat';
import ConversationList from './ConversationList';
import { getConversations, createConversation } from '@/services/api/agent-api';
import type { Conversation } from './types/agent-types';

interface ConversationListItem {
  id: string;
  title: string;
  clientId?: string;
  clientName?: string;
  messageCount: number;
  updatedAt: any;
}
import logger from '@/lib/logging';

const AgentPage: React.FC = () => {
  const { user } = useAuth();
  const { client: selectedClient } = useClient();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // State
  const [conversations, setConversations] = useState<ConversationListItem[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A question typed in the coach chat sheet arrives as router state: it opens a new
  // conversation and is sent as its first message.
  const location = useLocation();
  const navigate = useNavigate();
  const [firstMessage] = useState<string | null>(() => {
    const state = location.state as { firstMessage?: unknown } | null;
    return typeof state?.firstMessage === 'string' && state.firstMessage.trim() ? state.firstMessage : null;
  });
  const [firstMessageConversationId, setFirstMessageConversationId] = useState<string | null>(null);
  const firstMessageStarted = useRef(false);

  // Load conversations on mount
  useEffect(() => {
    loadConversations();
  }, []);

  // Handle URL params for conversation selection
  useEffect(() => {
    const convId = searchParams.get('conversation');
    if (convId) {
      setActiveConversationId(convId);
    }
  }, [searchParams]);

  const loadConversations = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const convList = await getConversations({ limit: 50 });
      setConversations(convList);
      
      // If no active conversation and we have conversations, select the first one
      // (unless a question from the coach chat sheet is opening its own conversation).
      if (!activeConversationId && !firstMessage && convList.length > 0) {
        setActiveConversationId(convList[0].id);
      }
    } catch (err: any) {
      logger.error('Failed to load conversations:', err);
      setError(err.message || 'Failed to load conversations');
    } finally {
      setIsLoading(false);
    }
  }, [activeConversationId, firstMessage]);

  const handleNewConversation = useCallback(async (): Promise<string | null> => {
    try {
      const conversationId = await createConversation({
        title: 'New Conversation',
        clientId: selectedClient?.id,
        clientName: selectedClient 
          ? `${selectedClient.firstName} ${selectedClient.lastName}`.trim()
          : undefined,
      });
      
      // Add to list and select
      const newConv: ConversationListItem = {
        id: conversationId,
        title: 'New Conversation',
        clientId: selectedClient?.id,
        clientName: selectedClient 
          ? `${selectedClient.firstName} ${selectedClient.lastName}`.trim()
          : undefined,
        messageCount: 0,
        updatedAt: new Date(),
      };
      
      setConversations(prev => [newConv, ...prev]);
      setActiveConversationId(conversationId);
      setSearchParams({ conversation: conversationId });
      setIsSidebarOpen(false);
      return conversationId;
    } catch (err: any) {
      logger.error('Failed to create conversation:', err);
      setError(err.message || 'Failed to create conversation');
      return null;
    }
  }, [selectedClient, setSearchParams]);

  // Start the conversation for the coach chat question once, then drop the router state
  // so a reload does not ask it again.
  useEffect(() => {
    if (!firstMessage || firstMessageStarted.current) return;
    firstMessageStarted.current = true;
    navigate(location.pathname, { replace: true, state: null });
    void handleNewConversation().then((conversationId) => {
      if (conversationId) setFirstMessageConversationId(conversationId);
    });
  }, [firstMessage, handleNewConversation, location.pathname, navigate]);

  const handleSelectConversation = useCallback((conversationId: string) => {
    setActiveConversationId(conversationId);
    setSearchParams({ conversation: conversationId });
    setIsSidebarOpen(false);
  }, [setSearchParams]);

  const handleDeleteConversation = useCallback((conversationId: string) => {
    setConversations(prev => prev.filter(c => c.id !== conversationId));
    if (activeConversationId === conversationId) {
      const remaining = conversations.filter(c => c.id !== conversationId);
      setActiveConversationId(remaining.length > 0 ? remaining[0].id : null);
    }
  }, [activeConversationId, conversations]);

  const handleConversationUpdated = useCallback((conversationId: string, title: string) => {
    setConversations(prev => 
      prev.map(c => c.id === conversationId ? { ...c, title } : c)
    );
  }, []);

  return (
    <PageLayout
      title="Harry Agent"
      subtitle="AI assistant for assessments, drills, and training programs"
      compactContent
    >
      <div className="flex min-h-[min(720px,calc(100dvh-14rem))] overflow-hidden rounded-lg border border-line bg-surface">
        {/* Mobile: conversation drawer toggle */}
        <div className="fixed bottom-24 right-4 z-50 md:hidden">
          <Button
            variant="default"
            size="icon"
            className="h-12 w-12 rounded-full shadow-lg"
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            aria-label={isSidebarOpen ? 'Close conversations' : 'Open conversations'}
          >
            {isSidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>

        {/* Sidebar - Conversation List */}
        <div
          className={cn(
            'fixed inset-y-0 left-0 z-40 w-72 border-r border-line bg-surface transform transition-transform duration-200 ease-in-out md:relative md:translate-x-0',
            isSidebarOpen ? 'translate-x-0' : '-translate-x-full',
            'top-16 md:top-0',
          )}
        >
          <div className="flex h-full flex-col">
            <div className="border-b border-line p-4">
              <div className="mb-3 flex items-center gap-2">
                <Bot className="h-6 w-6 text-accent" />
                <h2 className="flex items-center gap-2 text-lg font-semibold">
                  Conversations
                  <span className="rounded border border-accent/40 bg-accent px-1.5 py-0.5 text-[10px] font-semibold text-accent-fg">
                    ALPHA
                  </span>
                </h2>
              </div>
              <Button onClick={handleNewConversation} className="w-full" size="sm">
                <Plus className="mr-2 h-4 w-4" />
                New Chat
              </Button>
            </div>

            {/* Conversation List */}
            <div className="flex-1 overflow-y-auto">
              {error && (
                <div className="p-4 text-sm text-status-deviation">
                  {error}
                </div>
              )}
              
              <ConversationList
                conversations={conversations}
                activeConversationId={activeConversationId}
                onSelectConversation={handleSelectConversation}
                onDeleteConversation={handleDeleteConversation}
                isLoading={isLoading}
              />
            </div>

            {/* User info */}
            {user && (
              <div className="border-t border-line p-4 text-sm text-muted">
                <div className="truncate">{user.email || 'Practitioner'}</div>
              </div>
            )}
          </div>
        </div>

        {isSidebarOpen && (
          <div
            className="fixed inset-0 z-30 bg-bg/80 md:hidden"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}

        <div className="flex min-w-0 flex-1 flex-col">
          {activeConversationId ? (
            <AgentChat
              conversationId={activeConversationId}
              clientId={selectedClient?.id}
              clientName={selectedClient 
                ? `${selectedClient.firstName} ${selectedClient.lastName}`.trim()
                : undefined
              }
              onConversationUpdated={handleConversationUpdated}
              initialMessage={
                firstMessage && activeConversationId === firstMessageConversationId ? firstMessage : undefined
              }
            />
          ) : (
            <EmptyState onNewChat={handleNewConversation} />
          )}
        </div>
      </div>
    </PageLayout>
  );
};

// Empty state component
const EmptyState: React.FC<{ onNewChat: () => void }> = ({ onNewChat }) => (
  <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
    <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mb-6">
      <Bot className="h-8 w-8 text-accent" />
    </div>
    <h2 className="text-2xl font-semibold mb-2">Welcome to Harry Agent</h2>
    <p className="text-muted max-w-md mb-6">
      I'm your AI assistant for movement practice. I can help you with client assessments, 
      drill selection, and creating training programs based on evidence-based principles.
    </p>
    <Button onClick={onNewChat} size="lg">
      <Plus className="h-5 w-5 mr-2" />
      Start a New Conversation
    </Button>
    
    <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg">
      <SuggestionCard
        title="Analyze Client"
        description="Review assessment data and get recommendations"
      />
      <SuggestionCard
        title="Find Drills"
        description="Search for exercises based on client needs"
      />
      <SuggestionCard
        title="Create Program"
        description="Build a training plan following best practices"
      />
      <SuggestionCard
        title="Learn"
        description="Ask questions about biomechanics and movement"
      />
    </div>
  </div>
);

const SuggestionCard: React.FC<{ title: string; description: string }> = ({ title, description }) => (
  <div className="p-4 rounded-lg border border-line bg-surface/50 text-left hover:bg-surface transition-colors cursor-pointer">
    <h3 className="font-medium mb-1">{title}</h3>
    <p className="text-sm text-muted">{description}</p>
  </div>
);

export default AgentPage;





